import { useState, useEffect } from "react";
import { useGuaranteeVerify } from "@/hooks/useGuaranteeVerify";

interface GuaranteeSectionProps {
  defaultId?: string;
}

export default function GuaranteeSection({ defaultId }: GuaranteeSectionProps) {
  const [inputVal, setInputVal] = useState(defaultId || "");
  const { result, loading, verify, reset } = useGuaranteeVerify();

  useEffect(() => {
    if (defaultId) {
      setInputVal(defaultId);
      verify(defaultId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultId]);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setInputVal(text.trim());
      reset();
    } catch {
      // ignore
    }
  };

  const handleVerify = () => verify(inputVal);

  return (
    <section id="guarantee" className="w-full py-5 md:py-10 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-10">
        <div className="flex items-center gap-2 mb-4 md:mb-6">
          <span className="w-1 h-5 rounded-full bg-red-500 flex-shrink-0"></span>
          <h2 className="text-base md:text-lg font-bold text-gray-800">担保验证</h2>
        </div>

        <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
          {/* 验证框 */}
          <div className="w-full md:max-w-md bg-white rounded-2xl border border-gray-100 p-5 md:p-7">
            <p className="text-sm text-gray-500 mb-5">首创担保与理赔一体化服务!</p>

            <div className="flex items-center gap-2 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 mb-4">
              <i className="ri-search-line text-gray-400 text-base flex-shrink-0"></i>
              <input
                type="text"
                value={inputVal}
                onChange={(e) => { setInputVal(e.target.value); reset(); }}
                onKeyDown={(e) => e.key === "Enter" && handleVerify()}
                placeholder="请输入ID，ID是理赔唯一依据"
                className="flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder-gray-400"
              />
              <button
                onClick={handlePaste}
                className="text-xs text-sky-500 hover:text-sky-600 font-medium cursor-pointer whitespace-nowrap transition-colors px-1"
              >
                粘贴
              </button>
            </div>

            <button
              onClick={handleVerify}
              disabled={!inputVal.trim() || loading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-red-500 to-orange-400 text-white font-semibold text-base disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-opacity hover:opacity-90 whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>验证中...
                </span>
              ) : "验证"}
            </button>

            {result && (
              <div className={`mt-4 p-4 rounded-xl text-sm text-left transition-all ${
                result.status === "success"
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              }`}>
                <div className="flex items-start gap-2">
                  <i className={`text-base mt-0.5 flex-shrink-0 ${
                    result.status === "success"
                      ? "ri-checkbox-circle-fill text-green-500"
                      : "ri-close-circle-fill text-red-500"
                  }`}></i>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${
                      result.status === "success" ? "text-green-700" : "text-red-700"
                    }`}>{result.message}</p>
                    {result.data && (
                      <div className="mt-2 space-y-1 text-gray-600 text-xs">
                        {result.data.name && (
                          <p>名称：<span className="font-medium">{result.data.name}</span></p>
                        )}
                        {result.data.remark && (
                          <p>备注：<span className="font-medium">{result.data.remark}</span></p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-4 leading-relaxed">
              <span className="font-medium text-gray-600">合作ID是理赔的唯一依据!</span>
              <br />
              <span className="text-red-400">使用前请先验证网址是否加入担保,未来才能放心的使用或发起理赔。</span>
            </p>
          </div>

          {/* PC端右侧说明 */}
          <div className="hidden md:flex flex-1 flex-col justify-center gap-5 py-4">
            {[
              { icon: "ri-shield-check-line", title: "官方认证", desc: "所有担保ID均经过官方审核认证，确保合作安全可靠" },
              { icon: "ri-file-list-3-line", title: "理赔依据", desc: "合作ID是发起理赔的唯一依据，请务必在合作前完成验证" },
              { icon: "ri-customer-service-2-line", title: "全程跟进", desc: "专属客服全程跟进，遇到问题随时联系官方处理" },
            ].map(item => (
              <div key={item.icon} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 flex-shrink-0">
                  <i className={`${item.icon} text-red-500 text-lg`}></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-1">{item.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
